/**
 * Care Plan Review Page
 * 
 * Shows the AI-generated care plan for review and approval.
 * Implements the formal Review & Approve flow.
 * 
 * Philosophy: "AI Assists. People Decide."
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  ChevronLeft,
  Sparkles,
  Check,
  X,
  RefreshCw,
  FileText,
  ClipboardList,
  AlertCircle,
  Loader2,
  Send,
  ArrowLeft,
  Calendar,
  Clock,
  User,
  ShieldCheck,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Category colors
const CATEGORY_COLORS = {
  adl: "bg-blue-100 text-blue-800",
  iadl: "bg-green-100 text-green-800",
  companion: "bg-purple-100 text-purple-800",
  safety: "bg-red-100 text-red-800",
  medical: "bg-orange-100 text-orange-800",
};

export default function CarePlanReview() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [assessment, setAssessment] = useState(null);
  const [carePlan, setCarePlan] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [returnReason, setReturnReason] = useState("");

  // Fetch assessment and care plan
  const fetchData = async () => {
    try {
      // Fetch assessment
      const assessmentRes = await axios.get(`${API}/api/assessments/${assessmentId}`, {
        withCredentials: true,
      });
      setAssessment(assessmentRes.data);

      // If pending review, fetch review data
      if (assessmentRes.data.status === "pending_review") {
        const reviewRes = await axios.get(`${API}/api/assessments/${assessmentId}/review`, {
          withCredentials: true,
        });
        setReviewData(reviewRes.data);
      }

      // Fetch care plan
      const carePlanRes = await axios.get(`${API}/api/assessments/${assessmentId}/care-plan`, {
        withCredentials: true,
      });
      setCarePlan(carePlanRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      toast.error("Failed to load care plan data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [assessmentId]);

  // Generate care plan
  const handleGenerateCarePlan = async (regenerate = false) => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/api/assessments/${assessmentId}/generate-care-plan`,
        { use_ai: true, regenerate },
        { withCredentials: true }
      );
      setCarePlan({
        care_plan: response.data.care_plan,
        narrative: response.data.narrative,
      });
      toast.success("Care plan generated successfully");
      fetchData();
    } catch (err) {
      console.error("Failed to generate care plan:", err);
      toast.error(err.response?.data?.detail || "Failed to generate care plan");
    } finally {
      setGenerating(false);
    }
  };

  // Submit for review
  const handleSubmitForReview = async () => {
    try {
      await axios.post(
        `${API}/api/assessments/${assessmentId}/submit-for-review`,
        {},
        { withCredentials: true }
      );
      toast.success("Submitted for review");
      fetchData();
    } catch (err) {
      console.error("Failed to submit for review:", err);
      toast.error(err.response?.data?.detail || "Failed to submit for review");
    }
  };

  // Approve care plan
  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/api/assessments/${assessmentId}/approve`,
        {
          approval_notes: approvalNotes,
          effective_date: effectiveDate || null,
        },
        { withCredentials: true }
      );
      toast.success("Care plan approved successfully!");
      setShowApproveDialog(false);
      navigate("/assessments");
    } catch (err) {
      console.error("Failed to approve:", err);
      toast.error(err.response?.data?.detail || "Failed to approve care plan");
    } finally {
      setApproving(false);
    }
  };

  // Return for changes
  const handleReturnForChanges = async () => {
    if (!returnReason.trim()) {
      toast.error("Please provide a reason for returning");
      return;
    }
    try {
      await axios.post(
        `${API}/api/assessments/${assessmentId}/return-for-changes`,
        { reason: returnReason },
        { withCredentials: true }
      );
      toast.success("Returned for changes");
      setShowReturnDialog(false);
      navigate(`/assessments/${assessmentId}`);
    } catch (err) {
      console.error("Failed to return:", err);
      toast.error(err.response?.data?.detail || "Failed to return assessment");
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }

  if (!assessment) {
    return (
      <AppShell>
        <div className="text-center py-12">
          <p className="text-gray-500">Assessment not found</p>
          <Button onClick={() => navigate("/assessments")} className="mt-4">
            Back to Assessments
          </Button>
        </div>
      </AppShell>
    );
  }

  const demo = assessment.demographics || {};
  const clientName = `${demo.first_name || ""} ${demo.last_name || ""}`.trim() || "Client";
  const carePlanData = carePlan?.care_plan || assessment.ai_care_plan_draft || {};
  const services = carePlanData?.services || [];
  const canApprove = ["platform_owner", "agency_admin", "hr_staff"].includes(user?.role);
  const isReviewable = assessment.status === "pending_review";
  const hasDraft = assessment.status === "ai_draft_care_plan" || carePlanData?.services?.length > 0;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/assessments/${assessmentId}`)}
              className="mb-2"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Assessment
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              Care Plan: {clientName}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge variant="outline">
                {assessment.status?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
              {carePlanData?.method && (
                <Badge className="bg-purple-100 text-purple-800">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {carePlanData.method === "ai_enhanced" ? "AI Enhanced" : "Rule-based"}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {assessment.status === "assessment_complete" && (
              <Button
                onClick={() => handleGenerateCarePlan(false)}
                disabled={generating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Care Plan
              </Button>
            )}
            {assessment.status === "ai_draft_care_plan" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleGenerateCarePlan(true)}
                  disabled={generating}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <Button
                  onClick={handleSubmitForReview}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submit for Review
                </Button>
              </>
            )}
            {isReviewable && canApprove && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowReturnDialog(true)}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return for Changes
                </Button>
                <Button
                  onClick={() => setShowApproveDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve Care Plan
                </Button>
              </>
            )}
          </div>
        </div>

        {/* No Care Plan Yet */}
        {!hasDraft && assessment.status === "assessment_complete" && (
          <Card className="border-dashed">
            <CardContent className="text-center py-12">
              <Sparkles className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Ready to Generate Care Plan
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                The assessment is complete. Click the button above to have AI analyze the
                assessment and generate a personalized care plan with recommendations.
              </p>
              <Button
                onClick={() => handleGenerateCarePlan(false)}
                disabled={generating}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Care Plan
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Care Plan Content */}
        {hasDraft && (
          <Tabs defaultValue="services">
            <TabsList className="grid w-full grid-cols-4 h-auto">
              <TabsTrigger value="services" className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">Services</span>
              </TabsTrigger>
              <TabsTrigger value="narrative" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Narrative</span>
              </TabsTrigger>
              <TabsTrigger value="safety" className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Safety</span>
              </TabsTrigger>
              <TabsTrigger value="goals" className="flex items-center gap-2">
                <Target className="w-4 h-4" />
                <span className="hidden sm:inline">Goals</span>
              </TabsTrigger>
            </TabsList>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Recommended Services</CardTitle>
                  <CardDescription>
                    Services recommended based on the assessment. AI recommendations include reasoning.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {services.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No services recommended yet
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Service</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead>Reasoning</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {services.map((service, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {service.service_name}
                              {service.ai_recommended && (
                                <Badge className="ml-2 bg-purple-100 text-purple-800 text-xs">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  AI
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={CATEGORY_COLORS[service.category] || "bg-gray-100"}>
                                {service.category?.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell>{service.frequency || "As needed"}</TableCell>
                            <TableCell className="max-w-xs">
                              <p className="text-sm text-gray-600 truncate" title={service.reasoning}>
                                {service.reasoning || "-"}
                              </p>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Scheduling Recommendations */}
              {carePlanData?.scheduling_recommendations && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Scheduling Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold text-primary">
                          {carePlanData.scheduling_recommendations.recommended_hours_per_week || "-"}
                        </p>
                        <p className="text-sm text-gray-500">Hours/Week</p>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-lg font-semibold">
                          {carePlanData.scheduling_recommendations.visit_frequency || "-"}
                        </p>
                        <p className="text-sm text-gray-500">Visit Frequency</p>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-lg font-semibold">
                          {carePlanData.scheduling_recommendations.visit_duration_hours || "-"} hrs
                        </p>
                        <p className="text-sm text-gray-500">Per Visit</p>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-lg font-semibold">
                          {carePlanData.scheduling_recommendations.preferred_times || "-"}
                        </p>
                        <p className="text-sm text-gray-500">Preferred Time</p>
                      </div>
                    </div>
                    {carePlanData.scheduling_recommendations.rationale && (
                      <p className="mt-4 text-sm text-gray-600 bg-blue-50 p-3 rounded">
                        <strong>Rationale:</strong> {carePlanData.scheduling_recommendations.rationale}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Caregiver Requirements */}
              {carePlanData?.caregiver_requirements && Object.keys(carePlanData.caregiver_requirements).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Caregiver Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {carePlanData.caregiver_requirements.gender_preference && (
                        <div>
                          <Label className="text-sm text-gray-500">Gender Preference</Label>
                          <p className="font-medium">{carePlanData.caregiver_requirements.gender_preference}</p>
                        </div>
                      )}
                      {carePlanData.caregiver_requirements.language_requirement && (
                        <div>
                          <Label className="text-sm text-gray-500">Language Requirement</Label>
                          <p className="font-medium">{carePlanData.caregiver_requirements.language_requirement}</p>
                        </div>
                      )}
                      {carePlanData.caregiver_requirements.skills_required?.length > 0 && (
                        <div className="md:col-span-2">
                          <Label className="text-sm text-gray-500">Skills Required</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {carePlanData.caregiver_requirements.skills_required.map((skill, i) => (
                              <Badge key={i} variant="outline">{skill}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Narrative Tab */}
            <TabsContent value="narrative">
              <Card>
                <CardHeader>
                  <CardTitle>Narrative Care Plan</CardTitle>
                  <CardDescription>
                    Professional document for printing and compliance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {carePlan?.narrative || assessment.ai_care_plan_narrative ? (
                    <div className="prose max-w-none">
                      <div className="whitespace-pre-wrap text-sm leading-relaxed bg-gray-50 p-6 rounded-lg border">
                        {carePlan?.narrative || assessment.ai_care_plan_narrative}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      Narrative care plan will be generated with the structured plan
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Safety Tab */}
            <TabsContent value="safety">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5" />
                    Safety Considerations
                  </CardTitle>
                  <CardDescription>
                    Important safety information for caregivers
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {carePlanData?.safety_considerations?.length > 0 ? (
                    <ul className="space-y-2">
                      {carePlanData.safety_considerations.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No specific safety considerations documented
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Goals Tab */}
            <TabsContent value="goals">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Goals of Care
                  </CardTitle>
                  <CardDescription>
                    Measurable care goals based on assessment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {carePlanData?.goals_of_care?.length > 0 ? (
                    <ul className="space-y-2">
                      {carePlanData.goals_of_care.map((goal, index) => (
                        <li key={index} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{goal}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No specific goals documented yet
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Approve Dialog */}
        <AlertDialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Approve Care Plan</AlertDialogTitle>
              <AlertDialogDescription>
                This will make the care plan official and allow caregiver assignment.
                The care plan will become the source of truth for this client&apos;s care.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="effective_date">Effective Date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="approval_notes">Approval Notes (optional)</Label>
                <Textarea
                  id="approval_notes"
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Add any notes about this approval..."
                  rows={3}
                />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleApprove}
                disabled={approving}
                className="bg-green-600 hover:bg-green-700"
              >
                {approving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Approve
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Return for Changes Dialog */}
        <AlertDialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Return for Changes</AlertDialogTitle>
              <AlertDialogDescription>
                This will return the assessment to the assessor for modifications.
                Please provide a reason for the return.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="return_reason">Reason for Return *</Label>
              <Textarea
                id="return_reason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="Explain what changes are needed..."
                rows={4}
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReturnForChanges}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Return for Changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
